import React, { useEffect } from "react";

import ReactDOM from "react-dom";

import { urlFromFilepath, thumbnailUrlFromFilepath } from "./index";
import "./styles.css";

interface IProps {
	images: { filepath: string }[];
	currentIndex: number;
	display: "none" | "block";
	setDisplay: React.Dispatch<React.SetStateAction<"none" | "block">>;
	onNext: () => void;
	onPrev: () => void;
	onSelectIndex: (index: number) => void;
}

const CarouselModal: React.FC<IProps> = ({
	images,
	currentIndex,
	display,
	setDisplay,
	onNext,
	onPrev,
	onSelectIndex,
}) => {
	useEffect(() => {
		if (display === "none") return;
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "ArrowRight") onNext();
			else if (e.key === "ArrowLeft") onPrev();
			else if (e.key === "Escape") setDisplay("none");
		};
		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [display, onNext, onPrev, setDisplay]);

	if (display === "none") return null;

	const modal = (
		<div
			className="modal"
			onClick={(e) => {
				if (e.target === e.currentTarget) setDisplay("none");
			}}
		>
			<span className="close" onClick={() => setDisplay("none")}>
				&times;
			</span>

			<div className="modal-slider">
				{images.length > 1 && (
					<button className="modal-nav" onClick={onPrev}>
						<svg viewBox="0 0 24 24">
							<path fill="currentColor" d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
						</svg>
					</button>
				)}

				<div className="modal-image-container">
					<img
						key={currentIndex}
						className="modal-content"
						src={
							images[currentIndex].filepath.startsWith("http") || images[currentIndex].filepath.startsWith("/")
								? images[currentIndex].filepath
								: urlFromFilepath(images[currentIndex].filepath)
						}
						onError={({ currentTarget }) => {
							currentTarget.onerror = null;
							currentTarget.src = "/placeholder.png";
						}}
					/>
				</div>

				{images.length > 1 && (
					<button className="modal-nav" onClick={onNext}>
						<svg viewBox="0 0 24 24">
							<path fill="currentColor" d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
						</svg>
					</button>
				)}
			</div>

			<div className="modal-footer">
				{images.length > 1 && (
					<div className="modal-counter">
						{currentIndex + 1} / {images.length}
					</div>
				)}
				{images.length > 1 && (
					<div className="modal-thumbnails">
						{images.map((img, i) => (
							<div
								key={i}
								className={`modal-thumbnail-wrapper${i === currentIndex ? " modal-thumbnail-active" : ""}`}
								onClick={() => onSelectIndex(i)}
							>
								<img
									className="modal-thumbnail"
									src={thumbnailUrlFromFilepath(img.filepath)}
									onError={({ currentTarget }) => {
										currentTarget.onerror = null;
										currentTarget.src = "/placeholder.png";
									}}
								/>
								<span className="modal-thumbnail-number">{i + 1}</span>
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	);

	return ReactDOM.createPortal(modal, document.body);
};

export default CarouselModal;
